import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { getData, postData, putData } from "../../../webService/webservice.js";
import "../../../styles/Auth/booking.css";
import StoreMultiSelect from "../../Challange/storeMultiSelect.jsx";
import CustomerSelect from "../../Challange/CustomerSelect.jsx";
import ServiceSelect from "../../Challange/ServiceSelect.jsx";
import EmployeeSelect from "../../Challange/EmployeeSelect.jsx";
import TVFMLoader from "../Loader/loader.jsx";
import { Typography } from "@mui/material";
import { DateTime } from "luxon";
const AddBookingForm = ({
    isEditMode = false,
    bookingId = null,
    onSuccess,
    onCancel,
    initialStoreId,
    initialDate, // Optional: for Calendar date clicks
}) => {
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const sessionToken = localStorage.getItem("Token");

    const [loading, setLoading] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [errors, setErrors] = useState({});
    const [isAddCustomer, setIsAddCustomer] = useState(false);
    const [isAddService, setIsAddService] = useState(false);

    const [formData, setFormData] = useState({
        storeId: (initialStoreId && initialStoreId.length === 24) ? initialStoreId : "",
        storeName: "",
        customerId: "",
        customerName: "",
        customerPhone: "",
        serviceId: [],
        serviceName: [],
        serviceProvider: "",
        employeeId: [],
        employeeName: [],
        appointmentDate: initialDate ? (initialDate instanceof Date ? initialDate.toISOString().slice(0, 10) : initialDate) : "",
        timeSlot: "",
        status: "Pending",
        remarks: "",
    });

    const IST_ZONE = "Asia/Kolkata";

    const toUTCISOString = (date, time) => {
        return DateTime
            .fromFormat(`${date} ${time}`, "yyyy-MM-dd HH:mm", { zone: IST_ZONE })
            .toUTC()
            .toISO();
    };

    // Load existing booking in Edit mode
    useEffect(() => {
        if (isEditMode && bookingId) {
            const fetchBooking = async () => {
                try {
                    setLoading(true);
                    const response = await getData(`appointmentbookings/appointmentbooking/${bookingId}`, {}, sessionToken);
                    const data = response?.data;
                    if (data) {
                        const IST_ZONE = "Asia/Kolkata";
                        const dt = DateTime.fromISO(data.timeSlot, { zone: "utc" }).setZone(IST_ZONE);

                        const serDetails = data.serviceDetails || data.ServiceDetails || [];
                        const serIds = serDetails.length > 0 ? serDetails.map(s => s._id) : (Array.isArray(data.serviceId) ? data.serviceId.map(id => id._id || id) : (data.serviceId ? [data.serviceId._id || data.serviceId] : []));
                        const serNames = serDetails.length > 0 ? serDetails.map(s => s.serviceName) : (Array.isArray(data.serviceId) ? data.serviceId.map(s => s.serviceName || "") : []);
                        setFormData({
                            storeId: data.storeId?._id || data.storeId || "",
                            storeName: data.storeId?.storeName || data.storeDetails?.[0]?.storeName || "",
                            customerId: data.customerId?._id || data.customerId || "",
                            customerName: data.customerId?.name || data.CustomerDetails?.[0]?.name || "",
                            customerPhone: data.customerId?.phoneNumber || data.CustomerDetails?.[0]?.phoneNumber || "",
                            serviceId: serIds,
                            serviceName: serNames,
                            serviceProvider: data.serviceProvider || "",
                            employeeId: [], // Will be filled below
                            employeeName: [], // Will be filled below
                            appointmentDate: dt.toFormat("yyyy-MM-dd"),
                            timeSlot: dt.toFormat("HH:mm"),
                            status: data.status || "Pending",
                            remarks: data.remarks || "",
                        });

                        // Robustly extract employees after setting IDs and Names
                        const empDetails = data.EmployeeDetails || data.employeeDetails || [];
                        const rawEmployeeId = data.employeeId;
                        const isEmpArray = Array.isArray(rawEmployeeId);
                        let finalEmpIds = [];
                        let finalEmpNames = [];

                        if (isEmpArray && rawEmployeeId.length > 0) {
                            const mappedIds = rawEmployeeId.map(e => e?._id || e || "");
                            const mappedNames = mappedIds.map((id) => {
                                const details = empDetails.find(d => (d._id || d.id) === id);
                                if (details?.name || details?.employeeName) return details.name || details.employeeName;
                                const originalEmp = rawEmployeeId.find(e => (e?._id || e) === id);
                                if (originalEmp?.name || originalEmp?.employeeName) return originalEmp.name || originalEmp.employeeName;
                                return "";
                            });

                            if (mappedIds.length === 1 && serIds.length > 1) {
                                finalEmpIds = serIds.map(() => mappedIds[0]);
                                finalEmpNames = serIds.map(() => mappedNames[0]);
                            } else {
                                finalEmpIds = mappedIds;
                                finalEmpNames = mappedNames;
                            }
                        } else if (rawEmployeeId) {
                            const singleId = rawEmployeeId._id || rawEmployeeId;
                            const singleName = empDetails?.[0]?.name || rawEmployeeId?.name || "";
                            finalEmpIds = serIds.map(() => singleId);
                            finalEmpNames = serIds.map(() => singleName);
                        }

                        // Sync with service count
                        while (finalEmpIds.length < serIds.length) {
                            finalEmpIds.push("");
                            finalEmpNames.push("");
                        }
                        if (finalEmpIds.length > serIds.length) {
                            finalEmpIds = finalEmpIds.slice(0, serIds.length);
                            finalEmpNames = finalEmpNames.slice(0, serIds.length);
                        }

                        setFormData(prev => ({
                            ...prev,
                            employeeId: finalEmpIds,
                            employeeName: finalEmpNames
                        }));
                    }
                } catch (error) {
                    console.error("Error fetching booking:", error);
                    toast.error("Failed to load booking details");
                } finally {
                    setLoading(false);
                }
            };
            fetchBooking();
        }
    }, [isEditMode, bookingId, sessionToken]);

    useEffect(() => {
        const fetchMissingDetails = async () => {
            // Fetch Store Name
            if (formData.storeId && !formData.storeName) {
                try {
                    const res = await getData(`stores/store/${formData.storeId}`, {}, sessionToken);
                    if (res?.data) setFormData(prev => ({ ...prev, storeName: res.data.storeName }));
                } catch (e) { }
            }
            // Fetch Customer Details
            if (formData.customerId && (!formData.customerName || !formData.customerPhone)) {
                try {
                    const res = await getData(`customers/customer/${formData.customerId}`, {}, sessionToken);
                    if (res?.data) {
                        setFormData(prev => ({
                            ...prev,
                            customerName: res.data.name,
                            customerPhone: res.data.phoneNumber
                        }));
                    }
                } catch (e) { }
            }
            // Fetch Service Name (for multiple services if names are missing)
            if (formData.serviceId?.length > 0 && (!formData.serviceName || formData.serviceName.length === 0)) {
                try {
                    // Fetch each missing service name
                    const namePromises = formData.serviceId.map(async (id) => {
                        const res = await getData(`services/service/${id}`, {}, sessionToken);
                        return res?.data?.serviceName || "Unknown Service";
                    });
                    const names = await Promise.all(namePromises);
                    setFormData(prev => ({ ...prev, serviceName: names }));
                } catch (e) { }
            }
            // Fetch Employee Name (if array is empty or needs filling)
            if (Array.isArray(formData.employeeId) && formData.employeeId.length > 0) {
                const needsFilling = formData.employeeName.some((n, i) => formData.employeeId[i] && !n);
                if (needsFilling) {
                    try {
                        const namePromises = formData.employeeId.map(async (id, idx) => {
                            if (!id || formData.employeeName[idx]) return formData.employeeName[idx] || "";
                            const res = await getData(`users/user/${id}`, {}, sessionToken);
                            return res?.data?.name || "Unknown Artist";
                        });
                        const names = await Promise.all(namePromises);
                        setFormData(prev => ({ ...prev, employeeName: names }));
                    } catch (e) { }
                }
            }
        };
        fetchMissingDetails();
    }, [formData.storeId, formData.customerId, formData.serviceId, formData.employeeId, sessionToken]);

    const loadStores = async (inputValue, loadedOptions, { page }) => {
        try {
            const res = await getData("stores/stores", { page, limit: 10, search: inputValue }, sessionToken);
            const results = res?.data?.results || [];
            return {
                options: results.map(s => ({ value: s._id, label: s.storeName })),
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };
        } catch (err) { return { options: [], hasMore: false }; }
    };

    const loadCustomers = async (inputValue, loadedOptions, { page }) => {
        if (!formData.storeId) return { options: [], hasMore: false };
        try {
            const res = await getData("customers/customers", { page, limit: 10, search: inputValue, storeId: formData.storeId }, sessionToken);
            const results = res?.data?.[0]?.paginatedResults || [];
            return {
                options: results.map(c => ({ value: c._id, label: c.name, phone: c.phoneNumber })),
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };
        } catch (err) { return { options: [], hasMore: false }; }
    };

    const loadServices = async (inputValue, loadedOptions, { page }) => {
        if (!formData.storeId) return { options: [], hasMore: false };
        try {
            const res = await getData("services/services", { page, limit: 10, search: inputValue, storeId: formData.storeId }, sessionToken);
            const results = res?.data?.[0]?.paginatedResults || [];
            return {
                options: results.map(s => ({ value: s._id, label: s.serviceName })),
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };
        } catch (err) { return { options: [], hasMore: false }; }
    };

    const loadEmployees = async (inputValue, loadedOptions, { page }) => {
        if (!formData.storeId) return { options: [], hasMore: false };
        try {
            const res = await getData("users/users", {
                page,
                limit: 10,
                search: inputValue,
                storeId: formData.storeId,
                role: ["Stylist", "SeniorStylist", "Beautician"]
            }, sessionToken);
            const results = res?.data?.[0]?.paginatedResults || [];
            return {
                options: results.map(e => ({ value: e._id, label: e.name })),
                hasMore: results.length === 10,
                additional: { page: page + 1 }
            };
        } catch (err) { return { options: [], hasMore: false }; }
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.storeId) newErrors.storeId = "Store is required";
        if (!formData.customerId) newErrors.customerId = "Customer is required";
        if (!formData.serviceId || formData.serviceId.length === 0) newErrors.serviceId = "Service is required";
        if (!formData.appointmentDate) newErrors.appointmentDate = "Date is required";
        if (!formData.timeSlot) newErrors.timeSlot = "Time is required";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setErrors({});
        setShowLoader(true);

        try {
            const timeSlotISO = toUTCISOString(formData.appointmentDate, formData.timeSlot);
            const appointmentDateISO = formData.appointmentDate;

            const bookingData = {
                storeId: formData.storeId,
                serviceId: formData.serviceId,
                serviceProvider: formData.serviceProvider,
                customerId: formData.customerId,
                employeeId: formData.employeeId,
                appointmentDate: appointmentDateISO,
                timeSlot: timeSlotISO,
                remarks: formData.remarks,
                status: formData.status
            };

            let response;
            if (isEditMode) {
                response = await putData(`appointmentbookings/appointmentbooking/${bookingId}`, bookingData, sessionToken);
            } else {
                response = await postData("appointmentbookings/", bookingData, sessionToken);
            }

            if (response?.status === 200 || response?.success) {
                toast.success(response.message || (isEditMode ? "Booking updated successfully" : "Booking added successfully"));
                onSuccess?.();
            } else {
                toast.error(response?.message || "Failed to save booking");
            }
        } catch (error) {
            console.error("Save Booking Error:", error);
            toast.error(error.message || "Error saving booking");
        } finally {
            setShowLoader(false);
        }
    };

    return (
        <div
            className="add-Booking-form"
            style={{
                background: 'white',
                padding: '32px',
                maxHeight: '85vh',
                overflowY: 'auto',
                overflowX: 'hidden'
            }}
        >
            {showLoader && (
                <div className="scissor-overlay">
                    <div className="scissor-loader"><TVFMLoader /></div>
                </div>
            )}
            <Typography variant="h5" sx={{
                fontWeight: 700,
                mb: 4,
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <Icon icon="solar:calendar-add-bold-duotone" width="32" height="32" style={{ color: '#6366f1' }} />
                {isEditMode ? "Update Appointment" : "New Appointment"}
            </Typography>

            <form className="Booking-form" onSubmit={handleSubmit}>
                <div className="Booking-form-row">
                    <div className="booking-form-group">
                        <label className="input-label">Branch Name <span className="required">*</span></label>
                        <StoreMultiSelect
                            value={formData.storeId ? [{ value: formData.storeId, label: formData.storeName || "Selected Store" }] : []}
                            onChange={(val) => {
                                const selected = val && val.length > 0 ? val[0] : null;
                                setFormData(prev => ({
                                    ...prev,
                                    storeId: selected?.value || "",
                                    storeName: selected?.label || "",
                                    customerId: "",
                                    customerName: "",
                                    customerPhone: "",
                                    serviceId: [],
                                    serviceName: [],
                                    employeeId: [],
                                    employeeName: []
                                }));
                            }}
                            loadStores={loadStores}
                            loggedInRole={role}
                            assignedStoreIds={adminStoreIds}
                            isMulti={false}
                        />
                        {errors.storeId && <p className="field-error">{errors.storeId}</p>}
                    </div>

                    <div className="booking-form-group">
                        <label className="input-label">Customer Name <span className="required">*</span></label>
                        <CustomerSelect
                            value={formData.customerId ? { value: formData.customerId, label: formData.customerName } : null}
                            onChange={(val) => {
                                setFormData(prev => ({
                                    ...prev,
                                    customerId: val?.value || "",
                                    customerName: val?.label || "",
                                    customerPhone: val?.phone || ""
                                }));
                            }}
                            loadCustomers={loadCustomers}
                            showAddOption={false}
                            isDisabled={!formData.storeId}
                        />
                        {errors.customerId && <p className="field-error">{errors.customerId}</p>}
                    </div>
                </div>

                <div className="Booking-form-row" style={{ marginBottom: "24px" }}>
                    <div className="booking-form-group">
                        <label className="input-label" style={{ color: '#475569', fontWeight: 600 }}>Mobile Number</label>
                        <div style={{ position: 'relative' }}>
                            <Icon icon="solar:phone-bold-duotone" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 1 }} />
                            <input
                                type="text"
                                className="category-input"
                                value={formData.customerPhone || ""}
                                placeholder="Customer mobile number"
                                readOnly
                                style={{ paddingLeft: '40px', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}
                            />
                        </div>
                    </div>

                    <div className="booking-form-group">
                        <label className="input-label">Service Name <span className="required">*</span></label>
                        <ServiceSelect
                            isMulti={true}
                            value={
                                formData.serviceId?.map((id, index) => ({
                                    value: id,
                                    label: formData.serviceName[index]
                                })) || []
                            }
                            onChange={(val) => {
                                setFormData(prev => {
                                    const newServiceIds = val?.map(v => v.value) || [];
                                    const newServiceNames = val?.map(v => v.label) || [];
                                    const newEmployeeIds = Array.isArray(prev.employeeId) ? [...prev.employeeId] : [];
                                    const newEmployeeNames = Array.isArray(prev.employeeName) ? [...prev.employeeName] : [];

                                    if (newServiceIds.length > prev.serviceId.length) {
                                        for (let i = prev.serviceId.length; i < newServiceIds.length; i++) {
                                            newEmployeeIds.push("");
                                            newEmployeeNames.push("");
                                        }
                                    } else {
                                        newEmployeeIds.length = newServiceIds.length;
                                        newEmployeeNames.length = newServiceIds.length;
                                    }

                                    return {
                                        ...prev,
                                        serviceId: newServiceIds,
                                        serviceName: newServiceNames,
                                        employeeId: newEmployeeIds,
                                        employeeName: newEmployeeNames
                                    };
                                });
                            }}
                            loadServices={loadServices}
                            showAddOption={false}
                        />
                        {errors.serviceId && <p className="field-error">{errors.serviceId}</p>}
                    </div>
                </div>

                {formData.serviceId?.length > 0 && (
                    <div style={{
                        background: '#f8fafc',
                        padding: '20px',
                        borderRadius: '16px',
                        marginBottom: '24px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '12px', borderBottom: '1.5px solid #e2e8f0', paddingBottom: '10px' }}>
                            <label className='input-label' style={{ marginBottom: 0, fontWeight: 700, color: '#475569', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Service</label>
                            <label className='input-label' style={{ marginBottom: 0, fontWeight: 700, color: '#475569', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Assigned Artist</label>
                        </div>
                        <div style={{
                            maxHeight: '320px',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            scrollbarWidth: 'thin'
                        }}>
                            {formData.serviceId?.map((serviceId, index) => (
                                <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', alignItems: 'center', marginBottom: '12px' }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                                        {formData.serviceName[index] || `Service ${index + 1}`}
                                    </div>
                                    <div className='booking-form-group' style={{ marginBottom: 0 }}>
                                        <EmployeeSelect
                                            cacheUniqs={[serviceId]}
                                            value={formData.employeeId[index] ? { value: formData.employeeId[index], label: formData.employeeName[index] } : null}
                                            onChange={(val) => {
                                                setFormData(prev => {
                                                    const newIds = Array.isArray(prev.employeeId) ? [...prev.employeeId] : [];
                                                    const newNames = Array.isArray(prev.employeeName) ? [...prev.employeeName] : [];
                                                    newIds[index] = val?.value || "";
                                                    newNames[index] = val?.label || "";
                                                    return { ...prev, employeeId: newIds, employeeName: newNames };
                                                });
                                            }}
                                            loadEmployees={loadEmployees}
                                            isDisabled={!formData.storeId}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                    </div>
                )}

                <div className="Booking-form-row" style={{ marginBottom: '24px' }}>
                    <div className="booking-form-group">
                        <label className="input-label" style={{ color: '#475569', fontWeight: 600 }}>Appointment Date <span className="required">*</span></label>
                        <input
                            type="date"
                            className="booking-input"
                            value={formData.appointmentDate}
                            onChange={(e) => setFormData({ ...formData, appointmentDate: e.target.value })}
                            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                        />
                        {errors.appointmentDate && <p className="field-error">{errors.appointmentDate}</p>}
                    </div>

                    <div className="booking-form-group">
                        <label className="input-label" style={{ color: '#475569', fontWeight: 600 }}>Time Slot <span className="required">*</span></label>
                        <input
                            type="time"
                            className="booking-input"
                            value={formData.timeSlot}
                            onChange={(e) => setFormData({ ...formData, timeSlot: e.target.value })}
                            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                        />
                        {errors.timeSlot && <p className="field-error">{errors.timeSlot}</p>}
                    </div>
                </div>

                <div className="Booking-form-row" style={{ marginBottom: '32px' }}>
                    <div className="booking-form-group">
                        <label className="input-label" style={{ color: '#475569', fontWeight: 600 }}>Status <span className="required">*</span></label>
                        <select
                            className="category-input"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                        >
                            <option value="">Select status</option>
                            <option value="Completed">Completed</option>
                            <option value="Pending">Pending</option>
                            <option value="Cancelled">Cancelled</option>
                            <option value="Expired">Expired</option>
                            <option value="Postponed">Postponed</option>
                        </select>
                        {errors.status && <p className="field-error">{errors.status}</p>}
                    </div>

                    <div className="booking-form-group">
                        <label className="input-label" style={{ color: '#475569', fontWeight: 600 }}>Remarks</label>
                        <input
                            type="text"
                            className="booking-input"
                            placeholder="Any special requests or notes..."
                            value={formData.remarks}
                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                            style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}
                        />
                    </div>
                </div>

                <div className="Booking-buttons" style={{ borderTop: '1.5px solid #f1f5f9', paddingTop: '24px', gap: '16px' }}>
                    <button className="add-Product-button" type="submit" style={{
                        flex: 2,
                        height: '48px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}>
                        {isEditMode ? "Update Appointment" : "Confirm Booking"}
                    </button>
                    <button className="cancel-Product-button" type="button" onClick={onCancel} style={{
                        flex: 1,
                        height: '48px',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: 600,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        color: '#64748b',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddBookingForm;
